import React, { useState, useCallback, useRef } from "react";
import { Modal, Button, Input, Select, Space, Form } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import DataProvider from "../../services";
import {
  type ApiProfile,
  type ProfileFieldKey,
  getEmptyFields,
  PROFILE_FIELD_LABELS,
  PROFILE_FIELD_PLACEHOLDERS,
} from "../../lib/profile-completion";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"].map(
  (v) => ({ label: v, value: v })
);
const GENDERS = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
];

function parseList(value: string): string[] {
  if (!value || typeof value !== "string") return [];
  return value
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

interface ProfileCompleteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: ApiProfile | null;
  onDismissSession: () => void;
  onProfileUpdated?: (profile: ApiProfile) => void;
}

export function ProfileCompleteModal({
  open,
  onOpenChange,
  profile,
  onDismissSession,
  onProfileUpdated,
}: ProfileCompleteModalProps) {
  const emptyFields = getEmptyFields(profile);
  const [step, setStep] = useState<"intro" | "slides">("intro");
  const [slideIndex, setSlideIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localValues, setLocalValues] = useState<Record<string, unknown>>({});
  const continuingToSlidesRef = useRef(false);

  const currentField = emptyFields[slideIndex] as ProfileFieldKey | undefined;
  const isLastSlide = slideIndex >= emptyFields.length - 1;
  const isFirstSlide = slideIndex === 0;

  const handleBack = useCallback(() => {
    if (slideIndex > 0) {
      setSlideIndex((i) => i - 1);
      setError(null);
    }
  }, [slideIndex]);

  const handleIntroClose = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        if (continuingToSlidesRef.current) {
          continuingToSlidesRef.current = false;
        } else {
          onDismissSession();
        }
      }
      onOpenChange(nextOpen);
    },
    [onDismissSession, onOpenChange]
  );

  const handleContinue = useCallback(() => {
    continuingToSlidesRef.current = true;
    setStep("slides");
    setSlideIndex(0);
    setError(null);
    setLocalValues({});
  }, []);

  const getFieldValue = useCallback(
    (key: ProfileFieldKey): unknown => {
      if (localValues[key] !== undefined) return localValues[key];
      const p = profile as Record<string, unknown>;
      return p?.[key] ?? "";
    },
    [profile, localValues]
  );

  const setFieldValue = useCallback((key: string, value: unknown) => {
    setLocalValues((prev) => ({ ...prev, [key]: value }));
    setError(null);
  }, []);

  const buildPatchPayload = useCallback(
    (key: ProfileFieldKey): Record<string, unknown> => {
      const v = getFieldValue(key);
      switch (key) {
        case "dateOfBirth":
          return {
            dateOfBirth:
              v && String(v).trim() ? String(v).trim() : undefined,
          };
        case "height":
        case "weight":
        case "bloodGroup":
        case "gender":
          return {
            [key]: v && String(v).trim() ? String(v).trim() : undefined,
          };
        case "allergies":
        case "conditions":
        case "medications":
        case "diseaseBurdens": {
          const arr = Array.isArray(v) ? v : parseList(String(v ?? ""));
          return { [key]: arr };
        }
        case "emergencyContact": {
          const o =
            (v && typeof v === "object" ? v : {}) as Record<string, string>;
          return {
            emergencyContact: {
              name: (o.name ?? "").trim() || undefined,
              relationship: (o.relationship ?? "").trim() || undefined,
              phone: (o.phone ?? "").trim() || undefined,
              location: (o.location ?? "").trim() || undefined,
            },
          };
        }
        default:
          return {};
      }
    },
    [getFieldValue]
  );

  const handleSaveNext = useCallback(async () => {
    if (!currentField) return;
    const payload = buildPatchPayload(currentField);
    const key = currentField;
    const hasValue =
      key === "emergencyContact"
        ? (payload.emergencyContact as Record<string, string> | undefined) &&
          Object.values(
            payload.emergencyContact as Record<string, string>
          ).some((s) => s && String(s).trim())
        : payload[key] !== undefined &&
          payload[key] !== "" &&
          (Array.isArray(payload[key])
            ? (payload[key] as unknown[]).length > 0
            : true);
    if (!hasValue) {
      setError("Please enter a value or skip.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await DataProvider.updateProfile(payload);
      const updated = res?.data ?? res;
      onProfileUpdated?.(updated as ApiProfile);
      if (isLastSlide) {
        onDismissSession();
        onOpenChange(false);
      } else {
        setSlideIndex((i) => i + 1);
        setLocalValues((prev) => {
          const next = { ...prev };
          delete next[currentField];
          return next;
        });
      }
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof (err as { response?: { data?: { message?: string } } })
          .response?.data?.message === "string"
          ? (err as { response: { data: { message: string } } }).response.data
              .message
          : null;
      setError(message || "Failed to save. Please try again or skip.");
    } finally {
      setSaving(false);
    }
  }, [
    currentField,
    buildPatchPayload,
    isLastSlide,
    onDismissSession,
    onOpenChange,
    onProfileUpdated,
  ]);

  const handleSkip = useCallback(() => {
    setError(null);
    if (isLastSlide) {
      onDismissSession();
      onOpenChange(false);
    } else {
      setSlideIndex((i) => i + 1);
      setLocalValues((prev) => {
        const next = { ...prev };
        if (currentField) delete next[currentField];
        return next;
      });
    }
  }, [isLastSlide, currentField, onDismissSession, onOpenChange]);

  if (emptyFields.length === 0) return null;

  return (
    <>
      <Modal
        title="Complete your profile"
        open={open && step === "intro"}
        onCancel={() => handleIntroClose(false)}
        footer={[
          <Button key="close" onClick={() => handleIntroClose(false)}>
            Close
          </Button>,
          <Button
            key="continue"
            type="primary"
            onClick={handleContinue}
            style={{ backgroundColor: "#00A2F8", borderColor: "#00A2F8" }}
          >
            Continue
          </Button>,
        ]}
        closable
        maskClosable={false}
      >
        <p className="text-gray-600">
          Complete your profile to get the full experience. You can fill in your
          health details so we can serve you better. You can skip or do this
          later.
        </p>
      </Modal>

      <Modal
        title={
          <div className="flex items-center gap-2">
            {!isFirstSlide && (
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
                aria-label="Go back to previous field"
                className="flex items-center justify-center -ml-1"
              />
            )}
            <span>{currentField ? PROFILE_FIELD_LABELS[currentField] : ""}</span>
          </div>
        }
        open={open && step === "slides"}
        onCancel={() => {
          onDismissSession();
          onOpenChange(false);
        }}
        footer={[
          <Button key="skip" onClick={handleSkip} disabled={saving}>
            Skip
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={saving}
            onClick={handleSaveNext}
            style={{ backgroundColor: "#00A2F8", borderColor: "#00A2F8" }}
          >
            {isLastSlide ? "Save" : "Save & Next"}
          </Button>,
        ]}
        closable
        maskClosable={false}
      >
        <Space direction="vertical" className="w-full" size="middle">
          <p className="text-gray-500 text-sm">
            Step {slideIndex + 1} of {emptyFields.length}.
          </p>
          {currentField === "dateOfBirth" && (
            <Form.Item label="Date of birth">
              <Input
                type="date"
                value={String(getFieldValue("dateOfBirth") || "").slice(0, 10)}
                onChange={(e) => setFieldValue("dateOfBirth", e.target.value)}
                aria-label="Date of birth"
              />
            </Form.Item>
          )}
          {currentField === "height" && (
            <Form.Item label="Height">
              <Input
                placeholder={PROFILE_FIELD_PLACEHOLDERS.height}
                value={String(getFieldValue("height") || "")}
                onChange={(e) => setFieldValue("height", e.target.value)}
                aria-label="Height"
              />
            </Form.Item>
          )}
          {currentField === "weight" && (
            <Form.Item label="Weight">
              <Input
                placeholder={PROFILE_FIELD_PLACEHOLDERS.weight}
                value={String(getFieldValue("weight") || "")}
                onChange={(e) => setFieldValue("weight", e.target.value)}
                aria-label="Weight"
              />
            </Form.Item>
          )}
          {currentField === "bloodGroup" && (
            <Form.Item label="Blood group">
              <Select
                placeholder="Select blood group"
                options={BLOOD_GROUPS}
                value={
                  String(getFieldValue("bloodGroup") || "") || undefined
                }
                onSelect={(v) => setFieldValue("bloodGroup", v)}
                className="w-full"
                aria-label="Blood group"
              />
            </Form.Item>
          )}
          {currentField === "gender" && (
            <Form.Item label="Gender">
              <Select
                placeholder="Select gender"
                options={GENDERS}
                value={String(getFieldValue("gender") || "") || undefined}
                onSelect={(v) => setFieldValue("gender", v)}
                className="w-full"
                aria-label="Gender"
              />
            </Form.Item>
          )}
          {(currentField === "allergies" ||
            currentField === "conditions" ||
            currentField === "medications" ||
            currentField === "diseaseBurdens") && (
            <Form.Item label={PROFILE_FIELD_LABELS[currentField]}>
              <Input.TextArea
                placeholder={PROFILE_FIELD_PLACEHOLDERS[currentField] ?? "e.g. item1, item2"}
                value={
                  Array.isArray(getFieldValue(currentField))
                    ? (getFieldValue(currentField) as string[]).join(", ")
                    : String(getFieldValue(currentField) || "")
                }
                onChange={(e) =>
                  setFieldValue(currentField, e.target.value)
                }
                rows={3}
                aria-label={PROFILE_FIELD_LABELS[currentField]}
              />
            </Form.Item>
          )}
          {currentField === "emergencyContact" && (
            <>
              <Form.Item label="Name">
                <Input
                  placeholder="Name"
                  value={
                    (
                      getFieldValue("emergencyContact") as
                        | Record<string, string>
                        | undefined
                    )?.name ?? ""
                  }
                  onChange={(e) =>
                    setFieldValue("emergencyContact", {
                      ...((getFieldValue(
                        "emergencyContact"
                      ) as Record<string, string>) ?? {}),
                      name: e.target.value,
                    })
                  }
                  aria-label="Emergency contact name"
                />
              </Form.Item>
              <Form.Item label="Relationship">
                <Input
                  placeholder="Relationship"
                  value={
                    (
                      getFieldValue("emergencyContact") as
                        | Record<string, string>
                        | undefined
                    )?.relationship ?? ""
                  }
                  onChange={(e) =>
                    setFieldValue("emergencyContact", {
                      ...((getFieldValue(
                        "emergencyContact"
                      ) as Record<string, string>) ?? {}),
                      relationship: e.target.value,
                    })
                  }
                  aria-label="Relationship"
                />
              </Form.Item>
              <Form.Item label="Phone">
                <Input
                  placeholder="Phone"
                  type="tel"
                  value={
                    (
                      getFieldValue("emergencyContact") as
                        | Record<string, string>
                        | undefined
                    )?.phone ?? ""
                  }
                  onChange={(e) =>
                    setFieldValue("emergencyContact", {
                      ...((getFieldValue(
                        "emergencyContact"
                      ) as Record<string, string>) ?? {}),
                      phone: e.target.value,
                    })
                  }
                  aria-label="Emergency contact phone"
                />
              </Form.Item>
              <Form.Item label="Location">
                <Input
                  placeholder="Location"
                  value={
                    (
                      getFieldValue("emergencyContact") as
                        | Record<string, string>
                        | undefined
                    )?.location ?? ""
                  }
                  onChange={(e) =>
                    setFieldValue("emergencyContact", {
                      ...((getFieldValue(
                        "emergencyContact"
                      ) as Record<string, string>) ?? {}),
                      location: e.target.value,
                    })
                  }
                  aria-label="Emergency contact location"
                />
              </Form.Item>
            </>
          )}
          {error && (
            <p className="text-red-600 text-sm" role="alert">
              {error}
            </p>
          )}
        </Space>
      </Modal>
    </>
  );
}
